import React, { useEffect, useState } from 'react';
import {
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { InvestigationPlan } from './types';

interface ApprovedPlanChecklistProps {
  plan: InvestigationPlan;
  onChecklistUpdate: (updatedMarkdown: string) => void;
}

function parseChecklist(
  markdown: string,
): Array<{ label: string; checked: boolean }> {
  return markdown
    .split('\n')
    .map((line) => {
      const checked = /^- \[x\] /i.test(line);
      const label = line.replace(/^- \[[ x]\] /i, '').trim();
      return label ? { label, checked } : null;
    })
    .filter(
      (item): item is { label: string; checked: boolean } => item !== null,
    );
}

function serializeChecklist(
  items: Array<{ label: string; checked: boolean }>,
): string {
  return items
    .map(({ label, checked }) => `- [${checked ? 'x' : ' '}] ${label}`)
    .join('\n');
}

export const ApprovedPlanChecklist: React.FC<ApprovedPlanChecklistProps> = ({
  plan,
  onChecklistUpdate,
}) => {
  const [items, setItems] = useState(() =>
    plan.checklist ? parseChecklist(plan.checklist) : [],
  );

  useEffect(() => {
    setItems(plan.checklist ? parseChecklist(plan.checklist) : []);
  }, [plan.checklist]);

  if (items.length === 0) return null;

  const handleToggle = (idx: number, checked: boolean) => {
    const updated = items.map((item, i) =>
      i === idx ? { ...item, checked } : item,
    );
    setItems(updated);
    onChecklistUpdate(serializeChecklist(updated));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="overline"
        fontWeight={600}
        color="text.secondary"
        sx={{ display: 'block', mb: 2 }}
      >
        Approved Investigation Plan
      </Typography>

      <FormGroup>
        {items.map(({ label, checked }, idx) => (
          <FormControlLabel
            key={idx}
            control={
              <Checkbox
                size="small"
                checked={checked}
                onChange={(e) => handleToggle(idx, e.target.checked)}
                sx={{
                  p: 0.75,
                  '& .MuiSvgIcon-root': { fontSize: 18 },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {label}
              </Typography>
            }
            sx={{
              alignItems: 'flex-start',
              mb: 2.5,
              ml: 0,
              '&:last-of-type': { mb: 0 },
              '& .MuiFormControlLabel-label': { mt: '2px', ml: 1 },
            }}
          />
        ))}
      </FormGroup>
    </Paper>
  );
};
